// Contacts tab row edit dialog — one Contact, Company, or Entity linked to
// the Property: its own HubSpot fields, its role (association label) on
// this Property, and Unlink. Staged like the other link dialogs; Save sends
// only the fields that changed, and closes once everything went through.

import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";

import { OptionsField, RoleSelect } from "./LinkedRecordsSection";
import LinkDialogFooter from "./LinkDialogFooter";
import { KIND_CONFIG } from "./linkStaging";
import { EMPTY_LOOKUPS, loadPropertyLinkLookups, savePropertyLinks } from "./propertyLinkLookups";
import "./LinkedRecordsDialog.css";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import LinkOffIcon from "@mui/icons-material/LinkOff";

// Per kind: [label, HubSpot property, record field it's prefilled from, layout].
// layout "wide" spans the row; "city" fields share one City/State/Zip row;
// "options:<lookup>" renders a dropdown from the lookups.
const FIELD_LAYOUT = {
	contact: [
		["First Name", "firstname", "firstName"],
		["Last Name", "lastname", "lastName"],
		["Job Title", "jobtitle", "jobTitle", "wide"],
		["Email", "email", "email", "wide"],
		["Phone", "phone", "phone"],
		["Mobile", "mobilephone", "mobilePhone"],
	],
	company: [
		["Company Name", "name", "name"],
		["Type", "type", "type", "options:companyTypes"],
		["Domain", "domain", "domain"],
		["Phone", "phone", "phone"],
	],
	entity: [
		["Legal Name", "entity_name", "name"],
		["Entity Type", "entity_type", "type", "options:entityTypes"],
		["Address 1", "entity_address_1", "address1", "wide"],
		["Address 2", "entity_street_address_2", "address2", "wide"],
		["City", "entity_city", "city", "city"],
		["State", "entity_state", "state", "city"],
		["Zip", "zip_code", "zip", "city"],
		["Phone", "phone_number", "phone"],
	],
};

const fieldsFromRecord = (kind, record) =>
	Object.fromEntries((FIELD_LAYOUT[kind] || []).map(([, prop, from]) => [prop, String(record?.[from] ?? "")]));

const asTrimmed = (value) => String(value ?? "").trim();

const NAME_PROPS = { contact: ["firstname", "lastname"], company: ["name"], entity: ["entity_name"] };

const firstTypeId = (record) => String((record?.roleTypeIds || [])[0] ?? "");

// The parent keys this dialog by record, so each record gets a fresh
// instance and state can start straight from it.
const LinkedRecordEditDialog = ({ open, kind, record, property, employeeID, onClose, onSaved }) => {
	const { instance, accounts } = useMsal();

	const [fields, setFields] = useState(() => fieldsFromRecord(kind, record));
	const [typeId, setTypeId] = useState(() => firstTypeId(record));
	const [lookups, setLookups] = useState(EMPTY_LOOKUPS);
	const [saving, setSaving] = useState(false);
	const [errors, setErrors] = useState([]);
	const [confirmUnlink, setConfirmUnlink] = useState(false);

	const origFields = fieldsFromRecord(kind, record);
	const origTypeIds = (record?.roleTypeIds || []).map(String);
	const origTypeId = firstTypeId(record);

	useEffect(() => {
		if (!open) return;
		loadPropertyLinkLookups(instance, accounts[0])
			.then(setLookups)
			.catch((err) => setErrors([err?.response?.data?.error || "Failed to load roles from HubSpot."]));
	}, [open, instance, accounts]);

	if (!kind || !record) return null;

	const label = KIND_CONFIG[kind].label;
	const roles = lookups.roles[kind] || [];
	// Iterates this kind's fields (not whatever keys `fields` holds).
	const changedProps = Object.keys(origFields).filter((k) => asTrimmed(fields[k]) !== asTrimmed(origFields[k]));
	const roleChanged = typeId !== origTypeId;
	const parts = [];
	if (changedProps.length) parts.push(`${changedProps.length} field${changedProps.length > 1 ? "s" : ""} edited`);
	if (roleChanged) parts.push("role change");
	const dirty = parts.length > 0;

	const setField = (prop) => (e) => {
		const value = e.target.value;
		setFields((current) => ({ ...current, [prop]: value }));
		setErrors([]);
	};

	const handleClose = () => {
		if (saving) return;
		onClose();
	};

	const save = (changes) => {
		setSaving(true);
		setErrors([]);
		return savePropertyLinks(instance, accounts[0], property.id, changes, employeeID)
			.then((data) => {
				const { errors: opErrors = [], ...section } = data;
				if (section.contacts) onSaved(section);
				if (opErrors.length) {
					setErrors(opErrors);
					return;
				}
				onClose();
			})
			.catch((err) => setErrors([err?.response?.data?.error || err?.message || "Failed to save."]))
			.finally(() => setSaving(false));
	};

	const handleSave = () => {
		if (saving || !dirty) return;
		if (NAME_PROPS[kind].every((p) => !asTrimmed(fields[p]))) {
			setErrors([`${label} needs a name.`]);
			return;
		}
		const displayName = record.name;
		save({
			edits: changedProps.length
				? [{ kind, id: record.id, name: displayName, fields: Object.fromEntries(changedProps.map((p) => [p, fields[p]])) }]
				: [],
			roleChanges: roleChanged ? [{ kind, id: record.id, name: displayName, fromTypeIds: origTypeIds, toTypeId: typeId || null }] : [],
		});
	};

	const handleUnlink = () => {
		setConfirmUnlink(false);
		save({ removes: [{ kind, id: record.id, name: record.name }] });
	};

	const renderField = ([fieldLabel, prop, , layout]) => {
		if (layout?.startsWith("options:")) {
			return (
				<OptionsField
					key={prop}
					label={fieldLabel}
					options={lookups[layout.slice("options:".length)] || []}
					value={fields[prop] ?? ""}
					onChange={setField(prop)}
					disabled={saving}
				/>
			);
		}
		return (
			<TextField
				key={prop}
				className={layout === "wide" ? "linkDialogSpanAll" : undefined}
				label={fieldLabel}
				size="small"
				fullWidth
				value={fields[prop] ?? ""}
				onChange={setField(prop)}
				disabled={saving}
			/>
		);
	};

	const layout = FIELD_LAYOUT[kind];
	const cityFields = layout.filter((f) => f[3] === "city");
	const firstCityIndex = layout.findIndex((f) => f[3] === "city");

	return (
		<>
			<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ className: "linkDialogPaper" }}>
				<DialogTitle className="propertyViewerEditDialogTitle">
					Edit {label}
					{property?.name && (
						<Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 400, mt: 0.75 }}>
							{property.name}
						</Typography>
					)}
				</DialogTitle>

				<DialogContent className="linkDialogContent">
					<div className="linkDialogGrid2">
						{layout.map((f, i) => {
							if (f[3] !== "city") return renderField(f);
							if (i !== firstCityIndex) return null;
							return (
								<div key="cityRow" className="linkDialogCityRow linkDialogSpanAll">
									{cityFields.map(renderField)}
								</div>
							);
						})}
					</div>

					<div className="linkDialogOwners">
						<div className="linkDialogSectionHeader">
							<div className="linkDialogSectionTitle">On This Property</div>
						</div>
						<div className="linkDialogRecordLinkRow">
							{roles.length > 0 ? (
								<RoleSelect
									label="Role"
									className="linkDialogAddRole"
									roles={roles}
									value={typeId}
									onChange={(e) => {
										setTypeId(e.target.value);
										setErrors([]);
									}}
									allowNone
									disabled={saving}
								/>
							) : (
								<Typography variant="body2" color="text.secondary" className="linkDialogAddRole">
									HubSpot has no property roles set up for {KIND_CONFIG[kind].plural.toLowerCase()}.
								</Typography>
							)}
							<Button
								variant="outlined"
								color="error"
								startIcon={<LinkOffIcon />}
								onClick={() => setConfirmUnlink(true)}
								disabled={saving}
							>
								Unlink from Property
							</Button>
						</div>
					</div>
				</DialogContent>

				<LinkDialogFooter
					statusText={dirty ? `Unsaved: ${parts.join(", ")}` : ""}
					savedFlash={false}
					errors={errors}
					dirty={dirty}
					saving={saving}
					onCancel={handleClose}
					onSave={handleSave}
				/>
			</Dialog>

			<Dialog open={confirmUnlink} onClose={() => setConfirmUnlink(false)} maxWidth="xs" fullWidth>
				<DialogTitle className="propertyViewerEditDialogTitle">Unlink {label}?</DialogTitle>
				<DialogContent className="propertyViewerEditDialogContent">
					<Typography variant="body2">
						Remove <strong>{record.name}</strong> from this property? The {label.toLowerCase()} record itself stays in HubSpot.
						{dirty ? " Unsaved edits to it will be discarded." : ""}
					</Typography>
					<div className="dialogButtons">
						<Button variant="outlined" color="error" onClick={() => setConfirmUnlink(false)}>
							Cancel
						</Button>
						<Button variant="contained" color="error" disableElevation onClick={handleUnlink}>
							Unlink
						</Button>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default LinkedRecordEditDialog;
